import { useState, useCallback } from 'react';
import { useSearchParams } from "../../../../backend/navigation";
import { DISPATCH_COLUMN, GROUP_BY } from '../../../../constants';

/**
 * useDispatchUrlState - Hook for managing URL parameter synchronization
 *
 * This hook handles:
 * - Reading initial state from URL parameters on mount
 * - Providing a function to update URL parameters for shareable links
 * - Parsing URL values with proper defaults
 *
 * URL parameters supported:
 * - search: Search term
 * - dispatchStatus: Dispatch status filter
 * - state: State filter
 * - billStatus: Bill status filter
 * - page: Page number
 * - groupBy: Group by field
 * - groupItems: Selected group items (pipe-delimited)
 * - groupApplied: Whether groupBy is active
 * - billMode: Whether bill mode is active
 *
 * @returns {Object} URL state and updater
 */
export const useDispatchUrlState = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Parse initial state from URL parameters
   * Captured once as initial state; URL updates do not reinitialize local filters
   */
  const [initialState] = useState(() => {
    return {
      searchTerm: searchParams.get('search') || '',
      dispatchStatusFilter:
        searchParams.get('dispatchStatus') || DISPATCH_COLUMN[16].name,
      stateFilter: searchParams.get('state') || DISPATCH_COLUMN[17].name,
      billStatusFilter:
        searchParams.get('billStatus') || DISPATCH_COLUMN[15].name,
      pageNumber: parseInt(searchParams.get('page')) || 1,
      groupBy: searchParams.get('groupBy') || GROUP_BY.TITLE,
      groupItems: searchParams.get('groupItems')
        ? searchParams.get('groupItems').split('|')
        : [],
      groupApplied: searchParams.get('groupApplied') === 'true',
      billMode: searchParams.get('billMode') === 'true',
    };
  });

  /**
   * Updates URL query parameters to reflect current filter state
   * Only includes non-default values to keep URLs clean
   *
   * Uses { replace: true } to avoid polluting browser history with every filter change
   *
   * @param {Object} params - Object containing values to set in URL
   * @param {string} params.search - Search term
   * @param {string} params.dispatchStatus - Dispatch status filter
   * @param {string} params.state - State filter
   * @param {string} params.billStatus - Bill status filter
   * @param {number} params.page - Page number
   * @param {string} params.groupBy - Group by field
   * @param {Array} params.groupItems - Selected group items (will be joined with |)
   * @param {boolean} params.groupApplied - Whether groupBy is active
   * @param {boolean} params.billMode - Whether bill mode is active
   */
  const updateUrlParams = useCallback(
    (params = {}) => {
      const newParams = {};

      // Only include non-empty, non-default values to keep URL clean
      if (params.search) newParams.search = params.search;
      if (
        params.dispatchStatus &&
        params.dispatchStatus !== DISPATCH_COLUMN[16].name
      )
        newParams.dispatchStatus = params.dispatchStatus;
      if (params.state && params.state !== DISPATCH_COLUMN[17].name)
        newParams.state = params.state;
      if (params.billStatus && params.billStatus !== DISPATCH_COLUMN[15].name)
        newParams.billStatus = params.billStatus;
      if (params.page) newParams.page = params.page.toString();
      if (params.groupBy && params.groupBy !== GROUP_BY.TITLE)
        newParams.groupBy = params.groupBy;

      // Handle groupItems - extract only IDs before joining
      // Items may be [id, name] tuples or raw IDs
      if (params.groupItems && params.groupItems.length > 0) {
        const ids = params.groupItems.map((item) => {
          // If item is an array [id, name], extract only the ID
          if (Array.isArray(item) && item.length > 1) {
            return item[0];
          }
          // If it's already a string ID or simple value, use as is
          return item;
        });
        newParams.groupItems = ids.join('|');
      }

      if (params.groupApplied === true) newParams.groupApplied = 'true';
      if (params.billMode === true) newParams.billMode = 'true';

      setSearchParams(newParams, { replace: true });
    },
    [setSearchParams],
  );

  return {
    // Parsed initial state from URL
    initialState,

    // URL updater function
    updateUrlParams,

    // Raw searchParams for direct access if needed
    searchParams,
  };
};
